import React, { useState, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useMealplanning } from "../../hooks/useMealplanning";
import { useRecipes } from "../../hooks/useRecipes";
import { useShoppingLists } from "../../hooks/useShoppingLists";
import {
  Plus, Search, Sparkles, Link as LinkIcon, ChefHat, BookOpen,
  Utensils, X, ShoppingCart, Check, Calendar, ArrowRight, Trash2,
  MoreVertical, Edit3, ArrowRightCircle, CheckSquare, Square, Star, Heart
} from "lucide-react";
import { Badge, Button, Dropdown, Form, InputGroup } from "react-bootstrap";
import { DndContext, useDraggable, useDroppable, DragOverlay, pointerWithin, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

// Components
import RecipeDetailDialog from "./RecipeDetailDialog";
import ShoppingListSelectionDialog from "./ShoppingListSelectionDialog";
import RecipeView from "./RecipeView";
import RecipeCard from "./RecipeCard";
import RecipeEditDialog from "./RecipeEditDialog";
import ManualEntryDialog from "./ManualEntryDialog";
import PlanDateSelectionDialog from "./PlanDateSelectionDialog";
import PublishRecipeDialog from "./PublishRecipeDialog";
import { userService } from "../../services/userService";

// Constants
const MEAL_TYPE_BY_ID = {
  1: "Frühstück",
  2: "Mittagessen",
  3: "Abendessen",
  4: "Snack/Anderes",
};

const MEAL_TIME_ORDER = [1, 2, 3, 4];

// --- DRAG & DROP WRAPPERS ---

const DraggablePlan = ({ plan, children }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `plan-${plan.id}`,
    data: { type: 'plan', plan }
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ touchAction: 'none', cursor: 'grab' }}>
      {children}
    </div>
  );
};

const DraggableRecipe = ({ recipe, disabled, children }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `recipe-${recipe.id}`,
    data: { type: 'recipe', recipe },
    disabled
  });

  if (disabled) return children;

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ touchAction: 'none' }}>
      {children}
    </div>
  );
};

const DayDroppable = ({ date, isToday, isPast, isPlanning, children }) => {
  const dateIso = date.toISOString().split('T')[0];
  const { isOver, setNodeRef } = useDroppable({
    id: `day-${dateIso}`,
    data: { date: dateIso }
  });

  return (
    <div
      ref={setNodeRef}
      className={`p-2 transition-all border-bottom ${isOver ? 'bg-primary-subtle' : ''} ${isToday ? 'bg-success-subtle bg-opacity-10' : ''} ${isPast ? 'opacity-75' : ''} ${isPlanning ? 'cursor-pointer hover-bg-primary-subtle' : ''}`}
      style={{ minHeight: '80px', position: 'relative' }}
    >
      {children}
    </div>
  );
};

// --- INLINE PLAN FORM (C: replaces modal for new plans) ---
const InlinePlanForm = ({ recipe, date, onConfirm, onCancel }) => {
  const [mealTimeId, setMealTimeId] = useState(2);
  const [servings, setServings] = useState(recipe?.servings || 2);

  return (
    <div className="bg-primary-subtle border border-primary rounded-3 p-2 mt-2 animate-fade-in" onClick={e => e.stopPropagation()}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <Calendar size={14} className="text-primary" />
        <span className="small fw-bold text-truncate">{recipe?.title}</span>
        <button className="btn btn-link p-0 ms-auto text-muted" onClick={onCancel}><X size={14} /></button>
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Form.Select
          size="sm"
          value={mealTimeId}
          onChange={(e) => setMealTimeId(Number(e.target.value))}
          className="bg-white border-0 shadow-none"
          style={{ maxWidth: '130px', fontSize: '0.8rem' }}
        >
          {Object.entries(MEAL_TYPE_BY_ID).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </Form.Select>
        <div className="d-flex align-items-center bg-white rounded px-1 border" style={{ fontSize: '0.8rem' }}>
          <button className="btn btn-link text-dark p-0 px-1 fw-bold text-decoration-none btn-sm" onClick={() => setServings(Math.max(1, servings - 1))}>-</button>
          <span className="px-1 fw-bold" style={{ minWidth: '20px', textAlign: 'center' }}>{servings}</span>
          <button className="btn btn-link text-dark p-0 px-1 fw-bold text-decoration-none btn-sm" onClick={() => setServings(servings + 1)}>+</button>
        </div>
        <Button size="sm" variant="primary" className="px-2 py-1 d-flex align-items-center gap-1" onClick={() => onConfirm(date, mealTimeId, servings)} style={{ fontSize: '0.8rem' }}>
          <Check size={12} /> OK
        </Button>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

const Essensplanung = ({ auth }) => {
  const workspaceId = localStorage.getItem("workspaceId");

  // Data Hooks
  const { plans, favorites, addPlan, updatePlan, deletePlan, shiftPlans, fetchPlans, loading: loadingPlans } = useMealplanning({
    workspaceId,
    token: auth.user?.id_token,
  });

  const {
    publicRecipes, privateRecipes,
    publicPagination, privatePagination,
    loadMorePublic, loadMorePrivate,
    filters, setFilters,
    getRecipeById, addRecipe, removeRecipe, updateRecipe, optimizeRecipe,
    publishRecipe, unpublishRecipe,
    logFeedback, getFeedback, toggleFavorite,
    loading: loadingRecipes
  } = useRecipes({ token: auth.user?.id_token });

  const { lists: shoppingLists, historyItems, addBatchItems } = useShoppingLists({
    workspaceId,
    token: auth.user?.id_token
  });

  // Helper using pre-calculated stats from backend
  const getShoppingBadgeData = (plan) => {
    if (!plan.recipeId || !plan.ingredientStats) return null;
    const { total, added, purchased } = plan.ingredientStats;
    if (total === 0) return null;
    if (added === 0 && purchased === 0) return null;
    return { onList: added, purchased, total };
  };

  // UI State
  const [detailDialogState, setDetailDialogState] = useState({ show: false, data: null, isPlanningMode: false });
  const [shoppingDialogState, setShoppingDialogState] = useState({ show: false, mealPlan: null });
  const [manualEntryDialogState, setManualEntryDialogState] = useState({ show: false, date: null, data: null });
  const [editPlanDialogState, setEditPlanDialogState] = useState({ show: false, plan: null });
  const [publishDialogState, setPublishDialogState] = useState({ show: false, recipe: null });
  const [activeDragItem, setActiveDragItem] = useState(null);

  // Mobile Tab State
  const [mobileTab, setMobileTab] = useState('agenda');
  const [aiPrompt, setAiPrompt] = useState("");

  // View Mode
  const [viewMode, setViewMode] = useState('planning');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);

  // (B+C) Planning state: recipe being planned inline
  const [planningState, setPlanningState] = useState(null);
  // null | { recipe, targetDate: string | null }

  // (F) Bump mode
  const [bumpMode, setBumpMode] = useState(false);
  const [selectedBumpPlans, setSelectedBumpPlans] = useState([]);
  const [bumpDays, setBumpDays] = useState(1);

  // Desktop detection for enabling drag on recipe cards
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // (A) Scroll to today
  const todayRef = useRef(null);
  const agendaScrollRef = useRef(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkSub = async () => {
      try {
        const sub = await userService.getSubscription();
        setIsPro(sub && sub.status === 'active' && sub.plan_name !== 'Free');
      } catch (e) { console.error(e); }
    };
    const fetchUserPrefs = async () => {
      try {
        const me = await userService.getMe();
        setUserPreferences(me);
      } catch (e) { console.error("Failed to fetch user prefs", e); }
    };
    if (auth?.isAuthenticated) {
      checkSub();
      fetchUserPrefs();
    }
  }, [auth?.isAuthenticated]);

  // (A) Scroll to today when agenda becomes visible (mount or returning from recipe view)
  useEffect(() => {
    if (viewMode === 'planning' && !loadingPlans && todayRef.current && agendaScrollRef.current && !scrolledRef.current) {
      requestAnimationFrame(() => {
        if (!todayRef.current || !agendaScrollRef.current) return;
        const container = agendaScrollRef.current;
        const todayEl = todayRef.current;
        const containerRect = container.getBoundingClientRect();
        const todayRect = todayEl.getBoundingClientRect();
        container.scrollTop += todayRect.top - containerRect.top;
      });
      scrolledRef.current = true;
    }
    if (viewMode !== 'planning') {
      scrolledRef.current = false;
    }
  }, [viewMode, loadingPlans]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // (A) Derived Data: 7 past + today + 13 future = 21 days
  const agendaDays = useMemo(() => {
    return Array.from({ length: 21 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + (i - 7));
      return d;
    });
  }, []);

  // (D) Group plans by date and sort by meal time within each day
  const groupedPlans = useMemo(() => {
    const map = {};
    plans.forEach((p) => {
      const dateStr = p.planDate;
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(p);
    });
    Object.keys(map).forEach(key => {
      map[key].sort((a, b) => (a.mealTimeCodeId || 4) - (b.mealTimeCodeId || 4));
    });
    return map;
  }, [plans]);

  // (F) Past plans for bump mode
  const pastPlans = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    return plans.filter(p => p.planDate < todayStr);
  }, [plans]);

  // --- Handlers ---

  const handleDragStart = (event) => {
    setActiveDragItem(event.active.data.current);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);

    if (!over || !over.data.current?.date) return;

    const sourceType = active.data.current?.type;
    const targetDate = over.data.current?.date;

    if (sourceType === 'plan' && targetDate) {
      // Move existing plan to new date
      const p = active.data.current.plan;
      if (p.planDate !== targetDate) {
        await updatePlan(p.id, { ...p, planDate: targetDate });
      }
    } else if (sourceType === 'recipe' && targetDate) {
      // (B) Recipe dropped onto day — show inline form
      setPlanningState({ recipe: active.data.current.recipe, targetDate });
    }
  };

  const handleOpenDetail = async (dataOrId, isPlanningMode = false, plannedServings = null) => {
    let recipe = dataOrId;
    if (typeof dataOrId === 'number' || typeof dataOrId === 'string' || (dataOrId && !dataOrId.title)) {
      const id = typeof dataOrId === 'object' ? dataOrId.id : dataOrId;
      recipe = await getRecipeById(id);
      if (!recipe) {
        alert("Rezept konnte nicht geladen werden.");
        return;
      }
    }

    if (isPlanningMode) {
      setDetailDialogState({ show: true, data: recipe, isPlanningMode });
    } else {
      setSelectedRecipe(recipe);
      if (plannedServings) {
        recipe.plannedServings = plannedServings;
      }
      setViewMode('view');
    }
  };

  const handleOpenManualEntry = (date, existingPlan = null) => {
    let dateStr = date;
    if (existingPlan && !dateStr) {
      dateStr = existingPlan.planDate;
    }
    setManualEntryDialogState({
      show: true,
      date: typeof dateStr === 'string' ? dateStr : dateStr?.toISOString().split('T')[0],
      data: existingPlan ? { ...existingPlan, url: existingPlan.externalRecipeUrl, notes: existingPlan.notes } : null
    });
  };

  const handleSaveManualEntry = async (entryData) => {
    const payload = {
      workspaceId: Number(workspaceId),
      planDate: entryData.date,
      notes: entryData.title,
      externalRecipeUrl: entryData.link,
      mealTimeCodeId: entryData.mealTime,
      recipeId: null
    };
    if (entryData.id) {
      await updatePlan(entryData.id, { ...payload, id: entryData.id });
    } else {
      await addPlan(payload);
    }
    setManualEntryDialogState({ show: false, date: null, data: null });
  };

  // (B+C) Start planning flow: sets state, auto-switches to agenda on mobile
  const handlePlanRecipe = (recipe) => {
    setPlanningState({ recipe, targetDate: null });
    setDetailDialogState({ show: false, data: null, isPlanningMode: false });
    setMobileTab('agenda');
  };

  // (C) Day clicked while in planning mode (select target day)
  const handleDayClickForPlanning = (dateIso) => {
    if (planningState && !planningState.targetDate) {
      setPlanningState({ ...planningState, targetDate: dateIso });
    }
  };

  // (C) Confirm inline plan
  const handleConfirmInlinePlan = async (date, mealTimeId, servings) => {
    if (!planningState?.recipe) return;
    await addPlan({
      workspaceId: Number(workspaceId),
      planDate: date,
      notes: planningState.recipe.title,
      recipeId: planningState.recipe.id,
      mealTimeCodeId: mealTimeId,
      servings
    });
    setPlanningState(null);
  };

  // (E) Edit plan (date/time/servings) — via edit icon, not main click
  const handleEditPlan = (plan) => {
    setEditPlanDialogState({ show: true, plan });
  };

  const handleUpdatePlan = async (date, mealTimeId, servings) => {
    const plan = editPlanDialogState.plan;
    if (!plan) return;
    await updatePlan(plan.id, {
      ...plan,
      planDate: date,
      mealTimeCodeId: mealTimeId,
      servings
    });
    setEditPlanDialogState({ show: false, plan: null });
  };

  const handleAiPlan = () => {
    if (!aiPrompt.trim()) return;
    alert("AI Planning Agent is coming soon! Your prompt: " + aiPrompt);
    setAiPrompt("");
  };

  const handleCreateClick = () => {
    setSelectedRecipe(null);
    setViewMode('edit');
  };

  const handleSaveRecipe = async (payload) => {
    if (selectedRecipe && selectedRecipe.id) {
      await updateRecipe(selectedRecipe.id, payload);
    } else {
      await addRecipe(payload);
    }
    setViewMode('planning');
    setSelectedRecipe(null);
  };

  const handlePublishRequest = async (recipe) => {
    if (!recipe) return;
    if (recipe.editorialStatus === 'rejected') {
      if (window.confirm("Möchtest du den 'Abgelehnt' Status zurücksetzen und das Rezept wieder in einen Entwurf umwandeln?")) {
        try { await unpublishRecipe(recipe.id); } catch (e) { console.error(e); alert("Fehler beim Zurücksetzen."); }
      }
      return;
    }
    setPublishDialogState({ show: true, recipe });
  };

  const handleConfirmPublish = async () => {
    const r = publishDialogState.recipe;
    if (!r) return;
    try {
      await publishRecipe(r.id);
      setPublishDialogState({ show: false, recipe: null });
    } catch (e) { console.error(e); alert("Fehler beim Veröffentlichen"); }
  };

  // (F) Bump mode handlers
  const handleToggleBumpPlan = (planId) => {
    setSelectedBumpPlans(prev =>
      prev.includes(planId) ? prev.filter(id => id !== planId) : [...prev, planId]
    );
  };

  const handleBumpSelected = async () => {
    if (selectedBumpPlans.length === 0) return;
    const updates = selectedBumpPlans.map(planId => {
      const plan = plans.find(p => p.id === planId);
      if (!plan) return null;
      const newDate = new Date(plan.planDate);
      newDate.setDate(newDate.getDate() + bumpDays);
      return updatePlan(plan.id, { ...plan, planDate: newDate.toISOString().split('T')[0] });
    }).filter(Boolean);
    await Promise.all(updates);
    setSelectedBumpPlans([]);
    setBumpMode(false);
  };

  const handleSelectAllPastPlans = () => {
    if (selectedBumpPlans.length === pastPlans.length) {
      setSelectedBumpPlans([]);
    } else {
      setSelectedBumpPlans(pastPlans.map(p => p.id));
    }
  };

  // --- View mode renders ---

  if (viewMode === 'edit') {
    return (
      <RecipeEditDialog
        initialData={selectedRecipe}
        onBack={() => { setViewMode('planning'); setSelectedRecipe(null); }}
        onSave={handleSaveRecipe}
        onOptimize={optimizeRecipe}
        isPro={isPro}
        token={auth.user?.id_token}
      />
    );
  }

  if (viewMode === 'view' && selectedRecipe) {
    return (
      <RecipeView
        recipe={selectedRecipe}
        initialServings={selectedRecipe.plannedServings}
        onBack={() => { setViewMode('planning'); setSelectedRecipe(null); }}
        onEdit={() => setViewMode('edit')}
        onFork={async (forkedRecipe) => { setSelectedRecipe(forkedRecipe); setViewMode('edit'); }}
        logFeedback={logFeedback}
        getFeedback={getFeedback}
        showShoppingListAction={true}
      />
    );
  }

  // --- (D) Render plans grouped by meal time within a day ---

  const renderMealTimeGroups = (dayPlans, dateIso) => {
    const grouped = {};
    dayPlans.forEach(plan => {
      const mtId = plan.mealTimeCodeId || 4;
      if (!grouped[mtId]) grouped[mtId] = [];
      grouped[mtId].push(plan);
    });

    const activeMealTimes = MEAL_TIME_ORDER.filter(id => grouped[id]?.length > 0);
    if (activeMealTimes.length === 0) return null;

    const showHeaders = activeMealTimes.length > 1;

    return activeMealTimes.map(mtId => (
      <div key={mtId} className="mb-1">
        {showHeaders && (
          <div className="text-muted d-flex align-items-center gap-1 mb-1 mt-1" style={{ fontSize: '0.65rem' }}>
            <span className="text-uppercase fw-bold letter-spacing-wide">{MEAL_TYPE_BY_ID[mtId]}</span>
            <div className="flex-grow-1 border-bottom" style={{ opacity: 0.3 }}></div>
          </div>
        )}
        {grouped[mtId].map(plan => renderPlanItem(plan, dateIso, !showHeaders))}
      </div>
    ));
  };

  // --- (E) Render individual plan item ---

  const renderPlanItem = (plan, dateIso, showMealBadge) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const isPast = dateIso < todayStr;
    const isSelectingDay = planningState && !planningState.targetDate;

    return (
      <DraggablePlan key={plan.id} plan={plan}>
        <div
          className={`bg-white border rounded-3 p-2 shadow-sm d-flex align-items-center gap-2 mb-1 ${bumpMode && isPast ? '' : 'cursor-pointer hover-border-primary'}`}
          onClick={(e) => {
            // In day-selection planning mode, let click bubble to day handler
            if (isSelectingDay) return;

            if (bumpMode && isPast) {
              handleToggleBumpPlan(plan.id);
              return;
            }

            // (E) Click = view recipe (primary action)
            if (plan.recipeId) {
              handleOpenDetail(plan.recipeId, false, plan.servings);
            } else {
              handleOpenManualEntry(null, plan);
            }
          }}
        >
          {/* (F) Bump mode checkbox */}
          {bumpMode && isPast && (
            <div className="flex-shrink-0">
              {selectedBumpPlans.includes(plan.id)
                ? <CheckSquare size={16} className="text-primary cursor-pointer" />
                : <Square size={16} className="text-muted cursor-pointer" />
              }
            </div>
          )}

          <div className="flex-grow-1 overflow-hidden" style={{ lineHeight: 1.2 }}>
            <div className="d-flex align-items-center gap-1">
              {/* Meal type badge — inline change via dropdown */}
              {showMealBadge && (
                <Dropdown onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                  <Dropdown.Toggle as="div" className="badge bg-light text-dark border fw-normal text-truncate cursor-pointer hover-bg-gray" style={{ fontSize: '0.65rem', maxWidth: '80px' }}>
                    {MEAL_TYPE_BY_ID[plan.mealTimeCodeId] || "Mahlzeit"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
                    {Object.entries(MEAL_TYPE_BY_ID).map(([id, label]) => (
                      <Dropdown.Item
                        key={id}
                        onClick={() => updatePlan(plan.id, { ...plan, mealTimeCodeId: Number(id) })}
                        active={plan.mealTimeCodeId === Number(id)}
                        className="small"
                      >
                        {label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {plan.externalRecipeUrl && !plan.recipeId ? (
                <>
                  <span className="text-truncate fw-medium small mb-0">{plan.notes}</span>
                  <a
                    href={plan.externalRecipeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-1 text-muted hover-text-primary"
                    onClick={e => e.stopPropagation()}
                    title="Link öffnen"
                  >
                    <LinkIcon size={12} />
                  </a>
                </>
              ) : (
                <span className="text-truncate fw-medium small mb-0">{plan.recipeTitle || plan.notes}</span>
              )}

              {/* Shopping badges */}
              {(() => {
                const stats = getShoppingBadgeData(plan);
                if (!stats) return null;
                return (
                  <div className="ms-auto d-flex align-items-end gap-1">
                    {stats.onList > 0 && (
                      <Badge bg="warning" text="dark"
                        className="d-flex align-items-center gap-1 fw-normal border border-warning cursor-pointer hover-opacity-75"
                        style={{ fontSize: '0.6rem' }}
                        title={`${stats.onList} auf der Liste`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const r = await getRecipeById(plan.recipeId);
                          if (r) setShoppingDialogState({ show: true, mealPlan: plan, recipe: r });
                        }}
                      >
                        <ShoppingCart size={10} />{stats.onList}/{stats.total}
                      </Badge>
                    )}
                    {stats.purchased > 0 && (
                      <Badge bg="success"
                        className="d-flex align-items-center gap-1 fw-normal border border-success cursor-pointer hover-opacity-75"
                        style={{ fontSize: '0.6rem' }}
                        title={`${stats.purchased} bereits gekauft`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const r = await getRecipeById(plan.recipeId);
                          if (r) setShoppingDialogState({ show: true, mealPlan: plan, recipe: r });
                        }}
                      >
                        <Check size={10} />{stats.purchased}/{stats.total}
                      </Badge>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Action buttons */}
          <div className="d-flex align-items-center gap-1 ps-1 flex-shrink-0" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
            {/* Shopping cart when no badge shown */}
            {plan.recipeId && !getShoppingBadgeData(plan) && (
              <button
                className="btn btn-link text-muted p-1 sm-btn hover-text-primary"
                title="Zutaten einkaufen"
                onClick={async () => {
                  const r = await getRecipeById(plan.recipeId);
                  if (r) setShoppingDialogState({ show: true, mealPlan: plan, recipe: r });
                }}
              >
                <ShoppingCart size={13} />
              </button>
            )}
            {/* (E) Edit button — changes date/time/servings */}
            {plan.recipeId && (
              <button
                className="btn btn-link text-muted p-1 sm-btn hover-text-primary"
                title="Datum/Zeit ändern"
                onClick={() => handleEditPlan(plan)}
              >
                <Edit3 size={13} />
              </button>
            )}
            <button
              className="btn btn-link text-muted p-1 sm-btn hover-text-danger"
              onClick={() => deletePlan(plan.id)}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </DraggablePlan>
    );
  };

  // --- MAIN RENDER ---

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="d-flex flex-column flex-md-row flex-grow-1 bg-light position-relative" style={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}>

        {/* --- MOBILE TOGGLE --- */}
        <div className="d-md-none bg-white border-bottom p-2 flex-shrink-0 z-3">
          <div className="d-flex w-100 bg-light rounded-pill p-1 border">
            <button
              className={`btn btn-sm flex-fill rounded-pill fw-medium transition-all ${mobileTab === 'agenda' ? 'bg-white shadow-sm text-primary' : 'text-muted hover-bg-white-50'}`}
              onClick={() => setMobileTab('agenda')}
              style={{ border: 'none' }}
            >
              <div className="d-flex align-items-center justify-content-center gap-2">
                <Calendar size={16} /> Plan
                {planningState && !planningState.targetDate && (
                  <span className="badge bg-primary rounded-pill" style={{ fontSize: '0.6rem' }}>Wähle Tag</span>
                )}
              </div>
            </button>
            <button
              className={`btn btn-sm flex-fill rounded-pill fw-medium transition-all ${mobileTab === 'recipes' ? 'bg-white shadow-sm text-primary' : 'text-muted hover-bg-white-50'}`}
              onClick={() => setMobileTab('recipes')}
              style={{ border: 'none' }}
            >
              <div className="d-flex align-items-center justify-content-center gap-2">
                <Search size={16} /> Rezepte
              </div>
            </button>
          </div>
        </div>

        {/* --- SIDEBAR (AGENDA) --- */}
        <div
          className={`d-flex flex-column bg-white border-end shadow-sm agenda-sidebar ${mobileTab === 'recipes' ? 'd-none d-md-flex' : 'd-flex'}`}
          style={{ width: '100%', minWidth: '320px', overflow: 'hidden' }}
        >
          {/* Header */}
          <div className="p-3 border-bottom bg-white z-2">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                <Calendar className="text-primary" size={20} /> Dein Plan
              </h5>
              {/* (F) Bump mode toggle */}
              {pastPlans.length > 0 && !bumpMode && !planningState && (
                <button
                  className="btn btn-sm btn-outline-warning rounded-pill px-3 d-flex align-items-center gap-1"
                  onClick={() => { setBumpMode(true); setSelectedBumpPlans([]); setBumpDays(1); }}
                  title="Nicht gekochte Mahlzeiten verschieben"
                >
                  <ArrowRightCircle size={14} /> Verschieben
                </button>
              )}
              {bumpMode && (
                <button
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                  onClick={() => { setBumpMode(false); setSelectedBumpPlans([]); }}
                >
                  Abbrechen
                </button>
              )}
            </div>

            {/* (C) Planning mode banner */}
            {planningState && !planningState.targetDate && !bumpMode && (
              <div className="alert alert-primary py-2 px-3 mb-3 d-flex align-items-center justify-content-between small mb-0">
                <span><strong>{planningState.recipe.title}</strong> — Tippe auf einen Tag</span>
                <button className="btn btn-link p-0 text-primary" onClick={() => setPlanningState(null)}><X size={16} /></button>
              </div>
            )}

            {/* (F) Bump toolbar */}
            {bumpMode && (
              <div className="bg-warning-subtle border border-warning rounded-3 p-2 mb-0">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <button className="btn btn-sm btn-outline-dark rounded-pill px-2" onClick={handleSelectAllPastPlans} style={{ fontSize: '0.75rem' }}>
                    {selectedBumpPlans.length === pastPlans.length ? 'Keine' : 'Alle'}
                  </button>
                  <span className="small fw-bold">{selectedBumpPlans.length} ausgewählt</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">um</span>
                  <div className="d-flex align-items-center bg-white rounded px-1 border">
                    <button className="btn btn-link text-dark p-0 px-1 fw-bold text-decoration-none btn-sm" onClick={() => setBumpDays(Math.max(1, bumpDays - 1))}>-</button>
                    <span className="px-1 fw-bold small">{bumpDays}</span>
                    <button className="btn btn-link text-dark p-0 px-1 fw-bold text-decoration-none btn-sm" onClick={() => setBumpDays(bumpDays + 1)}>+</button>
                  </div>
                  <span className="small text-muted">{bumpDays === 1 ? 'Tag' : 'Tage'} verschieben</span>
                  <Button
                    size="sm"
                    variant="warning"
                    className="ms-auto px-3 rounded-pill fw-bold"
                    disabled={selectedBumpPlans.length === 0}
                    onClick={handleBumpSelected}
                  >
                    <ArrowRight size={14} /> Los
                  </Button>
                </div>
              </div>
            )}

            {/* AI Planner Input */}
            {!bumpMode && !planningState && (
              <div className="position-relative">
                <InputGroup size="sm">
                  <Form.Control
                    placeholder="Lass die KI deine Woche planen..."
                    aria-label="AI Prompt"
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAiPlan()}
                    className="bg-light border-0 shadow-none rounded-start-pill ps-3"
                  />
                  <Button variant="primary" className="rounded-end-pill px-3" onClick={handleAiPlan}>
                    <Sparkles size={14} />
                  </Button>
                </InputGroup>
              </div>
            )}
          </div>

          {/* Days List */}
          <div className="flex-grow-1 overflow-auto scrollbar-hide pb-6" ref={agendaScrollRef}>
            {agendaDays.map(date => {
              const dateIso = date.toISOString().split('T')[0];
              const today = new Date();
              const isToday = today.toDateString() === date.toDateString();
              const isPast = date.toDateString() !== today.toDateString() && date < today;
              const dayPlans = groupedPlans[dateIso] || [];
              const isSelectingDay = !!(planningState && !planningState.targetDate && !bumpMode);

              return (
                <DayDroppable key={dateIso} date={date} isToday={isToday} isPast={isPast} isPlanning={isSelectingDay}>
                  <div
                    ref={isToday ? todayRef : null}
                    onClick={() => isSelectingDay && handleDayClickForPlanning(dateIso)}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                      <div className={`fw-bold ${isToday ? 'text-success' : isPast ? 'text-muted' : 'text-dark'}`}>
                        {isToday && <span className="badge bg-success text-white me-1" style={{ fontSize: '0.6rem' }}>Heute</span>}
                        {date.toLocaleDateString('de-DE', { weekday: 'short' })} <span className="fw-normal text-muted ms-1">{date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span>
                      </div>

                      {!bumpMode && !isSelectingDay && (
                        <Dropdown as="div">
                          <Dropdown.Toggle as="button" className="btn btn-link p-0 text-muted shadow-none no-caret opacity-50 hover-opacity-100">
                            <MoreVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu align="end" className="shadow-sm border-0">
                            <Dropdown.Item onClick={() => shiftPlans(1, dateIso)} className="d-flex align-items-center gap-2"><ArrowRight size={14} /> Alle ab hier +1 Tag</Dropdown.Item>
                            <Dropdown.Item onClick={() => shiftPlans(-1, dateIso)} className="d-flex align-items-center gap-2 text-danger"><Trash2 size={14} /> Alle ab hier -1 Tag</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => handleOpenManualEntry(dateIso)} className="d-flex align-items-center gap-2 text-primary"><Plus size={14} /> Manueller Eintrag</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </div>

                    {/* (D) Meal-time grouped plans */}
                    <div className="d-flex flex-column">
                      {renderMealTimeGroups(dayPlans, dateIso)}

                      {dayPlans.length === 0 && !isSelectingDay && (
                        <div className="text-center py-2 px-3 border border-dashed rounded-3 text-muted opacity-25 small bg-light" style={{ fontSize: '0.75rem' }}>
                          Hierher ziehen
                        </div>
                      )}

                      {/* (C) "Tap here" affordance in planning mode */}
                      {isSelectingDay && (
                        <div className="text-center py-2 px-3 border border-primary border-dashed rounded-3 text-primary small bg-primary-subtle mt-1" style={{ fontSize: '0.8rem' }}>
                          Hier einplanen
                        </div>
                      )}

                      {/* (C) Inline plan form appears on selected day */}
                      {planningState?.targetDate === dateIso && (
                        <InlinePlanForm
                          recipe={planningState.recipe}
                          date={dateIso}
                          onConfirm={handleConfirmInlinePlan}
                          onCancel={() => setPlanningState(null)}
                        />
                      )}
                    </div>
                  </div>
                </DayDroppable>
              );
            })}
          </div>
        </div>

        {/* --- MAIN CONTENT (RECIPES) --- */}
        <div className={`flex-grow-1 d-flex flex-column h-100 overflow-auto pb-6 ${mobileTab === 'agenda' ? 'd-none d-md-flex' : 'd-flex'}`}>
          <div className="bg-white px-4 py-3 border-bottom shadow-sm sticky-top d-flex align-items-center justify-content-between" style={{ zIndex: 10 }}>
            <h6 className="m-0 fw-bold d-flex align-items-center gap-2">
              <Utensils className="text-primary" size={20} /> Rezeptsuche
            </h6>
            <button
              className="btn btn-sm btn-primary rounded-pill px-3 d-inline-flex align-items-center gap-2 flex-shrink-0"
              onClick={handleCreateClick}
            >
              <Plus size={16} /> Neues Rezept
            </button>
          </div>

          <div className="p-4" style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>

            <div className="bg-white rounded-4 shadow-sm p-3 mb-4 border">
              <div className="row g-3">
                <div className="col-md-6">
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><Search size={16} /></InputGroup.Text>
                    <Form.Control
                      placeholder="Suche nach Rezepten..."
                      className="bg-light border-0 shadow-none"
                      value={filters.search}
                      onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    />
                    {filters.search && <Button variant="light" onClick={() => setFilters(prev => ({ ...prev, search: '' }))}><X size={16} /></Button>}
                  </InputGroup>
                </div>
                <div className="col-md-3">
                  <Form.Control
                    placeholder="Tags (z.B. Vegan)"
                    className="bg-light border-0 shadow-none"
                    value={filters.tags.join(', ')}
                    onChange={e => {
                      const val = e.target.value;
                      const tags = val ? val.split(',').map(t => t.trim()).filter(Boolean) : [];
                      setFilters(prev => ({ ...prev, tags }));
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <Form.Select
                    className="bg-light border-0 shadow-none cursor-pointer"
                    value={filters.sort}
                    onChange={e => setFilters(prev => ({ ...prev, sort: e.target.value }))}
                  >
                    <option value="newest">Neueste zuerst</option>
                    <option value="popularity">Beliebteste</option>
                    <option value="title">Alphabetisch</option>
                    <option value="prep_time">Zubereitungszeit</option>
                  </Form.Select>
                </div>
              </div>
            </div>

            {/* Community Recipes */}
            <div className="mb-5">
              <h6 className="fw-bold text-muted text-uppercase mb-3 d-flex align-items-center gap-2">
                <Utensils size={16} /> Community Rezepte
              </h6>
              <div className="row g-3">
                {publicRecipes.map(recipe => (
                  <div key={recipe.id} className="col-sm-6 col-lg-4 col-xl-3">
                    {/* (B) Draggable on desktop only */}
                    <DraggableRecipe recipe={recipe} disabled={!isDesktop}>
                      <RecipeCard
                        recipe={recipe}
                        onEdit={() => handleOpenDetail(recipe)}
                        onDelete={() => removeRecipe(recipe.id)}
                        onClick={() => handleOpenDetail(recipe)}
                        onPlan={() => handlePlanRecipe(recipe)}
                        hideStatus={true}
                        hidePublicBadge={true}
                        planButtonPosition="overlay-top-right"
                        durationPosition="top-left"
                        showRating={true}
                        onToggleFavorite={() => toggleFavorite(recipe.id)}
                        isFavorited={recipe.isFavorited}
                      />
                    </DraggableRecipe>
                  </div>
                ))}
                {publicRecipes.length === 0 && !loadingRecipes && (
                  <div className="col-12 text-muted text-center py-4 small">
                    Keine öffentlichen Rezepte gefunden.
                  </div>
                )}
              </div>
              {publicPagination.hasMore && (
                <div className="text-center mt-3">
                  <Button variant="light" size="sm" onClick={loadMorePublic} disabled={loadingRecipes}>
                    {loadingRecipes ? 'Lädt...' : 'Mehr laden'}
                  </Button>
                </div>
              )}
            </div>

            {/* My Recipes */}
            <div>
              <h6 className="fw-bold text-muted text-uppercase mb-3 d-flex align-items-center gap-2">
                <ChefHat size={16} /> Meine Rezepte
              </h6>
              <div className="row g-3">
                {privateRecipes.map(recipe => (
                  <div key={recipe.id} className="col-12 col-lg-6 col-xl-4">
                    <div className="position-relative">
                      {/* (B) Draggable on desktop only */}
                      <DraggableRecipe recipe={recipe} disabled={!isDesktop}>
                        <RecipeCard
                          recipe={recipe}
                          onEdit={() => handleOpenDetail(recipe)}
                          onDelete={() => removeRecipe(recipe.id)}
                          onClick={() => handleOpenDetail(recipe)}
                          onTogglePublish={() => handlePublishRequest(recipe)}
                          onPlan={() => handlePlanRecipe(recipe)}
                          variant="horizontal"
                          hidePublicBadge={true}
                          planButtonPosition="overlay-top-right"
                          durationPosition="top-left"
                        />
                      </DraggableRecipe>
                    </div>
                  </div>
                ))}
                {privateRecipes.length === 0 && !loadingRecipes && (
                  <div className="col-12 text-muted text-center py-4 small">
                    Keine privaten Rezepte gefunden.
                  </div>
                )}
              </div>
              {privatePagination.hasMore && (
                <div className="text-center mt-3">
                  <Button variant="light" size="sm" onClick={loadMorePrivate} disabled={loadingRecipes}>
                    {loadingRecipes ? 'Lädt...' : 'Mehr laden'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drag overlay — shows dragged item preview */}
        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]} zIndex={10000}>
          {activeDragItem ? (
            <div
              className="bg-white rounded-3 shadow-lg p-3 border d-flex align-items-center gap-3"
              style={{ width: '280px', cursor: 'grabbing', pointerEvents: 'none' }}
            >
              <div className="rounded-circle bg-light p-2"><Utensils size={16} /></div>
              <div>
                <h6 className="mb-0 fw-bold fs-6 text-truncate">
                  {activeDragItem.type === 'recipe' ? activeDragItem.recipe?.title : activeDragItem.plan?.notes}
                </h6>
                <small className="text-info">{activeDragItem.type === 'recipe' ? 'Einplanen...' : 'Verschieben...'}</small>
              </div>
            </div>
          ) : null}
        </DragOverlay>

        {/* --- Dialogs --- */}

        <RecipeDetailDialog
          show={detailDialogState.show}
          initialData={detailDialogState.data}
          isPlanningMode={detailDialogState.isPlanningMode}
          onClose={() => setDetailDialogState({ show: false, data: null })}
          onSavePlan={() => {}}
          allRecipes={[...privateRecipes, ...publicRecipes]}
          onPlan={handlePlanRecipe}
          showShoppingListAction={true}
        />

        {/* PlanDateSelectionDialog kept for EDITING existing plans only */}
        <PlanDateSelectionDialog
          show={editPlanDialogState.show}
          onClose={() => setEditPlanDialogState({ show: false, plan: null })}
          onConfirm={handleUpdatePlan}
          recipeTitle={editPlanDialogState.plan?.recipeTitle || editPlanDialogState.plan?.notes}
          defaultServings={editPlanDialogState.plan?.servings || 2}
          initialDate={editPlanDialogState.plan?.planDate}
          initialMealTimeId={editPlanDialogState.plan?.mealTimeCodeId}
          isEditMode={true}
        />

        <ShoppingListSelectionDialog
          show={shoppingDialogState.show}
          mealPlan={shoppingDialogState.mealPlan}
          recipeData={shoppingDialogState.recipe}
          token={auth.user?.id_token}
          workspaceId={workspaceId}
          lists={shoppingLists}
          historyItems={historyItems}
          addBatchItems={addBatchItems}
          lastSelectedListId={userPreferences?.last_selected_list_for_batch_add_id || auth.user?.last_selected_list_for_batch_add_id}
          onSelectionChanged={(newListId) => {
            setUserPreferences(prev => ({ ...prev, last_selected_list_for_batch_add_id: newListId }));
          }}
          onClose={() => { setShoppingDialogState({ show: false, mealPlan: null }); fetchPlans(); }}
        />

        <ManualEntryDialog
          show={manualEntryDialogState.show}
          onClose={() => setManualEntryDialogState({ show: false, date: null, data: null })}
          onSave={handleSaveManualEntry}
          initialDate={manualEntryDialogState.date}
          initialData={manualEntryDialogState.data}
        />

        <PublishRecipeDialog
          show={publishDialogState.show}
          onHide={() => setPublishDialogState({ show: false, recipe: null })}
          onConfirm={handleConfirmPublish}
          recipeTitle={publishDialogState.recipe?.title}
        />

        <style>{`
           .scrollbar-hide::-webkit-scrollbar { display: none; }
           .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
           .hover-zoom-img img { transition: transform 0.3s ease; }
           .hover-border-primary:hover { border-color: #0d6efd !important; }
           .no-caret::after { display: none !important; }
           .dnd-active { user-select: none; }
           .pb-6 { padding-bottom: 5rem !important; }
           .hover-bg-white-50:hover { background-color: rgba(255,255,255,0.5); }
           .transition-all { transition: all 0.2s ease; }
           .animate-fade-in { animation: fadeIn 0.2s ease; }
           @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
           .hover-bg-primary-subtle:hover { background-color: rgba(13,110,253,0.1) !important; }

           @media (min-width: 768px) {
             .agenda-sidebar { max-width: 380px !important; width: 380px !important; flex-shrink: 0; }
           }
        `}</style>
      </div>
    </DndContext>
  );
};

export default Essensplanung;
